import { useBreakpoints } from "shared/lib/breakpoints/useBreakpoints"
import {
  BUILDING_REFERENCE_COLUMNS_BY_BREAKPOINT,
  HorizontalBreakpoint,
  getValueForBreakpoint
} from "shared/types/Breakpoints"

/**
 * Hook to calculate the number of columns based on container breakpoints.
 * Uses the standard useBreakpoints hook internally.
 *
 * @param element - The container element to observe
 * @param options - Optional configuration for column mapping
 * @returns The number of columns based on current breakpoint
 *
 * @example
 * ```tsx
 * const [element, setElement] = useState<HTMLDivElement | null>(null)
 * const columns = useResponsiveColumns(element)
 *
 * return <div ref={setElement}>...</div>
 * ```
 *
 * @example With custom column mapping
 * ```tsx
 * const columns = useResponsiveColumns(element, {
 *   columnsByBreakpoint: {
 *     [HorizontalBreakpoint.MOBILE_LANDSCAPE]: 2,
 *     [HorizontalBreakpoint.TABLET]: 3,
 *     [HorizontalBreakpoint.DESKTOP]: 4
 *   },
 *   defaultColumns: 4
 * })
 * ```
 */
export interface UseResponsiveColumnsOptions {
  columnsByBreakpoint?: Partial<Record<HorizontalBreakpoint, number>>
  defaultColumns?: number
}

export function useResponsiveColumns(
  element: HTMLElement | null,
  options?: UseResponsiveColumnsOptions
): number {
  const {
    columnsByBreakpoint = BUILDING_REFERENCE_COLUMNS_BY_BREAKPOINT,
    defaultColumns = 5
  } = options || {}

  const breakpoints = useBreakpoints(element)

  const columns = getValueForBreakpoint(breakpoints, columnsByBreakpoint)

  return columns ?? defaultColumns
}
